import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ScaleLoader from "react-spinners/ScaleLoader";
import { Link } from "react-router-dom";
import RestManStudioTree from "./RestManStudioTree";
import "./RestManStudioCollection.css";

const tabs = [
  "Request",
  "Auth",
  "Headers",
  "Query Params",
  "Body",
  "Response",
  "Debug Trace",
  "Environment",
  "Save",
];
const httpMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const localSaveUrl = "http://localhost:3001/restman-studio/collection";
const localTreeUrl = "http://localhost:3001/restman-studio/tree";
const localRequestUrl = "http://localhost:3001/restman-studio/request";
const localEnvironmentListUrl = "http://localhost:3001/restman-studio/environments";
const localEnvironmentUrl = "http://localhost:3001/restman-studio/environment";
const environmentNames = [
  "irealnd_dev",
  "ireland_test",
  "ireland_prod",
  "scania_china_dev",
  "scania_china_test",
];
const mandatoryHeaders = [
  {
    key: "x-correlation-id",
    value: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  },
  {
    key: "X-Client",
    value: "restman_studio",
  },
  {
    key: "Content-Type",
    value: "application/json",
  },
];
const mandatoryHeaderKeys = new Set(
  mandatoryHeaders.map((header) => header.key.toLowerCase())
);

const createRow = (overrides = {}) => ({
  id: `${Date.now()}-${Math.random()}`,
  enabled: true,
  key: "",
  value: "",
  ...overrides,
});

const createEnvironmentRow = (overrides = {}) => ({
  id: `${Date.now()}-${Math.random()}`,
  enabled: true,
  key: "",
  value: "",
  secret: false,
  ...overrides,
});

const createEnvironmentRows = (values = []) => {
  const rows = values.map((value) =>
    createEnvironmentRow({
      key: value.key || "",
      value: value.value || "",
      enabled: value.enabled !== false,
      secret: Boolean(value.secret),
    })
  );

  return rows.length ? rows : [createEnvironmentRow()];
};

const savedJwt = localStorage.getItem("token");

function RestManStudioCollection() {
  const [activeTab, setActiveTab] = useState("Request");
  const [method, setMethod] = useState("GET");
  const [requestUrl, setRequestUrl] = useState("");
  const [authToken, setAuthToken] = useState(savedJwt || "");
  const [headers, setHeaders] = useState([
    createRow({ key: "Accept", value: "application/json" }),
  ]);
  const [queryParams, setQueryParams] = useState([createRow()]);
  const [body, setBody] = useState("{\n  \n}");
  const [collectionFolder, setCollectionFolder] = useState("taurus api");
  const [requestName, setRequestName] = useState("");
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [response, setResponse] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [debugSteps, setDebugSteps] = useState([]);
  const [tree, setTree] = useState(null);
  const [treeError, setTreeError] = useState("");
  const [selectedRequestKey, setSelectedRequestKey] = useState("");
  const [environmentOptions, setEnvironmentOptions] = useState(environmentNames);
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    environmentNames[0]
  );
  const [environmentData, setEnvironmentData] = useState(null);
  const [environmentValues, setEnvironmentValues] = useState([
    createEnvironmentRow(),
  ]);
  const [environmentStatus, setEnvironmentStatus] = useState(null);
  const [environmentError, setEnvironmentError] = useState("");
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTree, setIsLoadingTree] = useState(false);
  const [isLoadingEnvironment, setIsLoadingEnvironment] = useState(false);
  const [isSavingEnvironment, setIsSavingEnvironment] = useState(false);

  const loadTree = useCallback(async () => {
    try {
      setIsLoadingTree(true);
      setTreeError("");
      const { data } = await axios.get(localTreeUrl);
      setTree(data);
    } catch (err) {
      setTreeError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load RestMan Studio tree."
      );
    } finally {
      setIsLoadingTree(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const loadEnvironment = useCallback(async (environmentName) => {
    if (!environmentName) {
      return;
    }

    try {
      setIsLoadingEnvironment(true);
      setEnvironmentError("");
      const { data } = await axios.get(
        `${localEnvironmentUrl}?name=${encodeURIComponent(environmentName)}`
      );

      setEnvironmentData(data.environment);
      setEnvironmentValues(createEnvironmentRows(data.environment?.values || []));
      setRevealedSecrets({});
      setEnvironmentStatus({
        action: "loaded",
        environmentName: data.environment?.name || environmentName,
        relativePath: data.relativePath,
      });
    } catch (err) {
      setEnvironmentError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load environment file."
      );
    } finally {
      setIsLoadingEnvironment(false);
    }
  }, []);

  useEffect(() => {
    const loadEnvironmentOptions = async () => {
      let names = environmentNames;

      try {
        const { data } = await axios.get(localEnvironmentListUrl);
        const apiNames = (data.environments || [])
          .map((environment) =>
            typeof environment === "string" ? environment : environment.name
          )
          .filter(Boolean);

        names = apiNames.length ? apiNames : environmentNames;
        setEnvironmentOptions(names);
      } catch (err) {
        setEnvironmentOptions(environmentNames);
        setEnvironmentError(
          err.response?.data?.message ||
            err.message ||
            "Unable to load environment list."
        );
      }

      const defaultEnvironment = names[0] || environmentNames[0];
      setSelectedEnvironment(defaultEnvironment);
      loadEnvironment(defaultEnvironment);
    };

    loadEnvironmentOptions();
  }, [loadEnvironment]);

  const selectedHeaders = useMemo(() => {
    const customHeaders = headers
      .filter((row) => row.enabled && row.key.trim())
      .reduce((acc, row) => {
        acc[row.key.trim()] = row.value;
        return acc;
      }, {});

    const requestHeaders = mandatoryHeaders.reduce(
      (acc, row) => ({
        ...acc,
        [row.key]: row.value,
      }),
      customHeaders
    );

    if (authToken.trim()) {
      const token = authToken.trim();
      requestHeaders.Authorization = token.toLowerCase().startsWith("bearer ")
        ? token
        : `Bearer ${token}`;
    }

    return requestHeaders;
  }, [authToken, headers]);

  const updateRow = (rows, setRows, id, field, value) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (rows, setRows, id) => {
    if (rows.length === 1) {
      setRows([createRow()]);
      return;
    }
    setRows(rows.filter((row) => row.id !== id));
  };

  const buildUrl = () => {
    const trimmedUrl = requestUrl.trim();
    if (!trimmedUrl) {
      throw new Error("Request URL is required.");
    }

    const url = new URL(trimmedUrl);
    queryParams
      .filter((row) => row.enabled && row.key.trim())
      .forEach((row) => url.searchParams.append(row.key.trim(), row.value));

    return url.toString();
  };

  const redactHeaders = (requestHeaders = {}) =>
    Object.entries(requestHeaders).reduce((acc, [key, value]) => {
      acc[key] = key.toLowerCase() === "authorization" ? "Bearer ******" : value;
      return acc;
    }, {});

  const headersForCollection = (requestHeaders = {}) =>
    Object.entries(requestHeaders)
      .filter(([key]) => key.toLowerCase() !== "authorization")
      .map(([key, value]) => ({
        key,
        value,
        type: "text",
        enabled: true,
      }));

  const isLikelyCorsOrPreflightError = (err) =>
    !err.response && err.message === "Network Error";

  const getBrowserRequestDetails = (err) => {
    const browserRequest = err.request || {};

    return {
      readyState: browserRequest.readyState,
      status: browserRequest.status,
      statusText: browserRequest.statusText,
      responseURL: browserRequest.responseURL,
      timeout: browserRequest.timeout,
      withCredentials: browserRequest.withCredentials,
    };
  };

  const getNetworkFailureHint = (err) => {
    if (!isLikelyCorsOrPreflightError(err)) {
      return "";
    }

    return [
      "Browser received no usable response for JavaScript.",
      "Most likely cause is CORS preflight failure.",
      "This request has Authorization, X-Client, x-correlation-id, and Content-Type: application/json, so the browser sends an OPTIONS preflight before the API call.",
      "If the API or gateway does not allow Origin http://localhost:3000, method OPTIONS, and headers authorization, content-type, x-client, x-correlation-id, Axios reports Network Error and response stays null.",
      "Check Chrome DevTools > Network for the OPTIONS request. Standalone API clients are not restricted by browser CORS.",
    ].join(" ");
  };

  const createTraceStep = (trace, status, source, details = {}) => {
    const entry = {
      time: new Date().toISOString(),
      status,
      source,
      details,
    };
    const nextTrace = [...trace, entry];
    setDebugSteps(nextTrace);
    return nextTrace;
  };

  const createErrorDetails = (err, context, finalUrl, eventMeta, trace) => {
    const responseStatus = err.response?.status;
    const responseStatusText = err.response?.statusText;
    const likelyCause = getNetworkFailureHint(err);

    return {
      title: responseStatus
        ? `Request failed with HTTP ${responseStatus}`
        : "Request failed before a response was received",
      message: err.message || "Unknown error",
      likelyCause,
      name: err.name || "Error",
      code: err.code || "",
      source: context,
      eventType: eventMeta.type,
      eventTrigger: eventMeta.trigger,
      method,
      url: finalUrl || requestUrl,
      status: responseStatus || "",
      statusText: responseStatusText || "",
      stack: err.stack || "No stack trace available from the browser.",
      debug: {
        checkpointTrace: trace,
        request: {
          method,
          url: finalUrl || requestUrl,
          headers: redactHeaders(selectedHeaders),
          body: method === "GET" ? undefined : body,
        },
        browserRequest: getBrowserRequestDetails(err),
        response: err.response
          ? {
              status: responseStatus,
              statusText: responseStatusText,
              headers: err.response.headers,
              data: err.response.data,
            }
          : null,
      },
    };
  };

  const createHttpStatusDetails = (result, finalUrl, eventMeta, trace) => ({
    title: `Request completed with HTTP ${result.status}`,
    message: result.statusText || "The API returned an error status.",
    likelyCause:
      "The browser reached the API and received an HTTP response. Check response body, status code, and backend logs using the correlation id.",
    name: "HttpStatusError",
    code: "",
    source: "Send request handler",
    eventType: eventMeta.type,
    eventTrigger: eventMeta.trigger,
    method,
    url: finalUrl,
    status: result.status,
    statusText: result.statusText,
    stack:
      "HTTP error status returned by the remote API. No JavaScript exception stack was generated.",
    debug: {
      checkpointTrace: trace,
      request: {
        method,
        url: finalUrl,
        headers: redactHeaders(selectedHeaders),
        body: method === "GET" ? undefined : body,
      },
      response: {
        status: result.status,
        statusText: result.statusText,
        headers: result.headers,
        data: result.data,
      },
    },
  });

  const parseUrlForCollection = (rawUrl) => {
    const url = new URL(rawUrl);
    const query = Array.from(url.searchParams.entries()).map(([key, value]) => ({
      key,
      value,
      enabled: true,
    }));

    return {
      raw: rawUrl,
      protocol: url.protocol.replace(":", ""),
      host: url.hostname.split("."),
      path: url.pathname.split("/").filter(Boolean),
      query,
    };
  };

  const createHeaderRowsFromCollection = (savedHeaders = []) => {
    const customHeaderRows = savedHeaders
      .filter(
        (header) =>
          header?.key &&
          header.enabled !== false &&
          !mandatoryHeaderKeys.has(header.key.toLowerCase())
      )
      .map((header) =>
        createRow({
          key: header.key,
          value: header.value || "",
          enabled: header.enabled !== false,
        })
      );

    return customHeaderRows.length
      ? customHeaderRows
      : [createRow({ key: "Accept", value: "application/json" })];
  };

  const createQueryRowsFromCollection = (savedQuery = []) => {
    const queryRows = savedQuery
      .filter((query) => query?.key)
      .map((query) =>
        createRow({
          key: query.key,
          value: query.value || "",
          enabled: query.enabled !== false,
        })
      );

    return queryRows.length ? queryRows : [createRow()];
  };

  const loadSavedRequest = (requestNode) => {
    const savedRequest = requestNode.request?.request || {};
    const savedAuthToken =
      savedRequest.auth?.bearer?.find((entry) => entry.key === "token")?.value ||
      "";

    setMethod(savedRequest.method || "GET");
    setRequestUrl(savedRequest.url?.raw || "");
    setRequestName(requestNode.name || "");
    setCollectionFolder(requestNode.collectionFolderName || collectionFolder);
    setHeaders(createHeaderRowsFromCollection(savedRequest.header || []));
    setQueryParams(createQueryRowsFromCollection(savedRequest.url?.query || []));
    setBody(savedRequest.body?.raw || "{\n  \n}");

    if (savedAuthToken && !savedAuthToken.includes("{{")) {
      setAuthToken(savedAuthToken);
    }

    setSelectedRequestKey(requestNode.uniqueKey || "");
    setSaveStatus(null);
    setSaveError("");
    setErrorDetails(null);
    setResponse(null);
    setActiveTab("Request");
  };

  const handleCreateTreeRequest = async (file, draft) => {
    try {
      setTreeError("");
      const { data } = await axios.post(localRequestUrl, {
        relativePath: file.relativePath,
        collectionFolderName: file.collectionFolderName || file.collectionName,
        method: draft.method,
        requestName: draft.requestName,
      });

      await loadTree();
      if (data.requestNode) {
        loadSavedRequest(data.requestNode);
      }
      setSaveStatus({
        action: data.action,
        requestName: data.requestName,
        relativePath: data.relativePath,
      });
    } catch (err) {
      setTreeError(
        err.response?.data?.message ||
          err.message ||
          "Unable to add request to collection."
      );
    }
  };

  const deriveRequestName = (rawUrl) => {
    try {
      const url = new URL(rawUrl);
      const lastPathPart =
        url.pathname.split("/").filter(Boolean).pop() || url.hostname;
      return `${method}_${lastPathPart}`;
    } catch (err) {
      return `${method}_request`;
    }
  };

  const getEffectiveRequestName = (finalUrl) =>
    requestName.trim() || deriveRequestName(finalUrl);

  const createCollectionRequest = (finalUrl) => {
    const name = getEffectiveRequestName(finalUrl);
    const request = {
      id:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`,
      name,
      uniqueKey: `${method}:${name}`,
      request: {
        auth: authToken.trim()
          ? {
              type: "bearer",
              bearer: [
                {
                  key: "token",
                  value: "{{put_access_token}}",
                  type: "string",
                },
              ],
            }
          : {
              type: "none",
            },
        method,
        header: headersForCollection(selectedHeaders),
        url: parseUrlForCollection(finalUrl),
      },
      response: [],
    };

    if (method !== "GET" && body.trim()) {
      request.request.body = {
        mode: "raw",
        raw: body,
        options: {
          raw: {
            language: "json",
          },
        },
      };
    }

    return request;
  };

  const handleSave = async () => {
    setSaveError("");
    setSaveStatus(null);

    try {
      setIsSaving(true);
      const finalUrl = buildUrl();
      const requestToSave = createCollectionRequest(finalUrl);
      const { data } = await axios.post(localSaveUrl, {
        apiFolderName: collectionFolder,
        collectionName: collectionFolder,
        request: requestToSave,
      });

      setSaveStatus(data);
      setRequestName(requestToSave.name);
      setSelectedRequestKey(requestToSave.uniqueKey);
      await loadTree();
      setActiveTab("Save");
    } catch (err) {
      setSaveError(
        err.response?.data?.message ||
          err.message ||
          "Unable to save local_pulse_tool collection."
      );
      setActiveTab("Save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnvironmentChange = async (event) => {
    const environmentName = event.target.value;

    setSelectedEnvironment(environmentName);
    setActiveTab("Environment");
    await loadEnvironment(environmentName);
  };

  const updateEnvironmentRow = (id, field, value) => {
    setEnvironmentValues(
      environmentValues.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const removeEnvironmentRow = (id) => {
    if (environmentValues.length === 1) {
      setEnvironmentValues([createEnvironmentRow()]);
      return;
    }

    setEnvironmentValues(environmentValues.filter((row) => row.id !== id));
  };

  const toggleSecretReveal = (id) => {
    setRevealedSecrets((current) => ({
      ...current,
      [id]: !current[id],
    }));
  };

  const createEnvironmentPayloadValues = () =>
    environmentValues
      .filter((row) => row.key.trim())
      .map((row) => ({
        key: row.key.trim(),
        value: row.value,
        enabled: row.enabled,
        secret: Boolean(row.secret),
      }));

  const handleSaveEnvironment = async () => {
    setEnvironmentError("");
    setEnvironmentStatus(null);

    try {
      setIsSavingEnvironment(true);
      const { data } = await axios.post(localEnvironmentUrl, {
        name: selectedEnvironment,
        values: createEnvironmentPayloadValues(),
      });

      setEnvironmentData(data.environment);
      setEnvironmentValues(createEnvironmentRows(data.environment?.values || []));
      setRevealedSecrets({});
      setEnvironmentStatus({
        action: data.action,
        environmentName: data.environment?.name || selectedEnvironment,
        relativePath: data.relativePath,
      });
    } catch (err) {
      setEnvironmentError(
        err.response?.data?.message ||
          err.message ||
          "Unable to save environment file."
      );
    } finally {
      setIsSavingEnvironment(false);
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const eventMeta = {
      type: event.type,
      trigger:
        event.nativeEvent?.submitter?.textContent?.trim() || "Form submit",
    };
    setErrorDetails(null);
    setResponse(null);
    setDebugSteps([]);
    let finalUrl = requestUrl;
    let trace = [];

    try {
      setIsSending(true);
      trace = createTraceStep(trace, "START", "Send button submit event", {
        event: eventMeta,
        method,
        rawUrl: requestUrl,
      });
      finalUrl = buildUrl();
      trace = createTraceStep(trace, "PASS", "URL built with query params", {
        url: finalUrl,
      });
      trace = createTraceStep(trace, "PASS", "Headers prepared", {
        headers: redactHeaders(selectedHeaders),
        mandatoryHeaders: mandatoryHeaders.map((row) => row.key),
        hasAuthorizationHeader: Boolean(selectedHeaders.Authorization),
      });

      const startedAt = performance.now();
      trace = createTraceStep(trace, "SEND", "Calling axios", {
        method,
        url: finalUrl,
      });
      const result = await axios({
        method,
        url: finalUrl,
        headers: selectedHeaders,
        data: method === "GET" || !body.trim() ? undefined : body,
        timeout: 60000,
        validateStatus: () => true,
      });
      const durationMs = Math.round(performance.now() - startedAt);
      trace = createTraceStep(trace, "RESPONSE", "Axios returned response", {
        status: result.status,
        statusText: result.statusText,
        durationMs,
      });

      setResponse({
        method,
        url: finalUrl,
        status: result.status,
        statusText: result.statusText,
        durationMs,
        headers: result.headers,
        data: result.data,
      });
      if (result.status >= 400) {
        trace = createTraceStep(trace, "HTTP_ERROR", "API returned error status", {
          status: result.status,
          statusText: result.statusText,
        });
        setErrorDetails(
          createHttpStatusDetails(result, finalUrl, eventMeta, trace)
        );
      }
      setActiveTab("Response");
    } catch (err) {
      trace = createTraceStep(trace, "FAIL", "Exception caught in handleSend", {
        name: err.name,
        message: err.message,
        code: err.code,
        hasResponse: Boolean(err.response),
        browserRequest: getBrowserRequestDetails(err),
      });
      setErrorDetails(
        createErrorDetails(
          err,
          "Send request handler",
          finalUrl,
          eventMeta,
          trace
        )
      );
      setResponse(
        err.response
          ? {
              method,
              url: requestUrl,
              status: err.response.status,
              statusText: err.response.statusText,
              headers: err.response.headers,
              data: err.response.data,
            }
          : null
      );
      setActiveTab("Response");
    } finally {
      setIsSending(false);
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string") {
      return value;
    }
    return JSON.stringify(value, null, 2);
  };

  const renderRowsTable = (rows, setRows, keyPlaceholder, valuePlaceholder) => (
    <table className="lpt-edit-table">
      <thead>
        <tr>
          <th className="lpt-check-column">Use</th>
          <th>Key</th>
          <th>Value</th>
          <th className="lpt-action-column">Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td className="lpt-check-column">
              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(event) =>
                  updateRow(rows, setRows, row.id, "enabled", event.target.checked)
                }
                aria-label="Enable row"
              />
            </td>
            <td>
              <input
                type="text"
                value={row.key}
                placeholder={keyPlaceholder}
                onChange={(event) =>
                  updateRow(rows, setRows, row.id, "key", event.target.value)
                }
              />
            </td>
            <td>
              <input
                type="text"
                value={row.value}
                placeholder={valuePlaceholder}
                onChange={(event) =>
                  updateRow(rows, setRows, row.id, "value", event.target.value)
                }
              />
            </td>
            <td className="lpt-action-column">
              <button
                type="button"
                className="lpt-secondary-button"
                onClick={() => removeRow(rows, setRows, row.id)}
              >
                Remove
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderEnvironmentTable = () => (
    <table className="lpt-edit-table lpt-environment-table">
      <thead>
        <tr>
          <th className="lpt-check-column">Use</th>
          <th>Variable</th>
          <th>Value</th>
          <th className="lpt-secret-column">Secret</th>
          <th className="lpt-action-column">Action</th>
        </tr>
      </thead>
      <tbody>
        {environmentValues.map((row) => {
          const isRevealed = Boolean(revealedSecrets[row.id]);
          const isSecretMasked = row.secret && !isRevealed;

          return (
            <tr key={row.id}>
              <td className="lpt-check-column">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(event) =>
                    updateEnvironmentRow(row.id, "enabled", event.target.checked)
                  }
                  aria-label="Enable environment variable"
                />
              </td>
              <td>
                <input
                  type="text"
                  value={row.key}
                  placeholder="Variable name"
                  onChange={(event) =>
                    updateEnvironmentRow(row.id, "key", event.target.value)
                  }
                />
              </td>
              <td>
                <input
                  type={isSecretMasked ? "password" : "text"}
                  value={row.value}
                  placeholder="Variable value"
                  onChange={(event) =>
                    updateEnvironmentRow(row.id, "value", event.target.value)
                  }
                />
              </td>
              <td className="lpt-secret-column">
                <label className="lpt-inline-check">
                  <input
                    type="checkbox"
                    checked={row.secret}
                    onChange={(event) =>
                      updateEnvironmentRow(row.id, "secret", event.target.checked)
                    }
                  />
                  Secret
                </label>
              </td>
              <td className="lpt-action-column lpt-environment-actions">
                {row.secret && (
                  <button
                    type="button"
                    className="lpt-secondary-button"
                    onClick={() => toggleSecretReveal(row.id)}
                  >
                    {isRevealed ? "Hide" : "Reveal"}
                  </button>
                )}
                <button
                  type="button"
                  className="lpt-secondary-button"
                  onClick={() => removeEnvironmentRow(row.id)}
                >
                  Remove
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <main className="local-pulse-page">
      <div className="lpt-shell">
      <RestManStudioTree
        tree={tree}
        treeError={treeError}
        isLoadingTree={isLoadingTree}
        selectedRequestKey={selectedRequestKey}
        onCreateRequest={handleCreateTreeRequest}
        onRefresh={loadTree}
        onSelectRequest={loadSavedRequest}
      />
      <form className="lpt-workspace" onSubmit={handleSend}>
        <header className="lpt-title-bar">
          <h1>RestMan Studio</h1>
          <div className="lpt-title-actions">
            <label className="lpt-environment-select">
              Environment
              <select
                value={selectedEnvironment}
                disabled={isLoadingEnvironment}
                onChange={handleEnvironmentChange}
              >
                {environmentOptions.map((environmentName) => (
                  <option key={environmentName} value={environmentName}>
                    {environmentName}
                  </option>
                ))}
              </select>
            </label>
            <Link className="lpt-title-link" to="/RestManStudioEnvironments">
              Edit Environments
            </Link>
          </div>
        </header>
        <section className="lpt-request-bar" aria-label="Request builder">
          <select value={method} onChange={(event) => setMethod(event.target.value)}>
            {httpMethods.map((httpMethod) => (
              <option key={httpMethod} value={httpMethod}>
                {httpMethod}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={requestUrl}
            placeholder="https://api.example.com/resource"
            onChange={(event) => setRequestUrl(event.target.value)}
          />
          <button type="submit" className="lpt-send-button" disabled={isSending}>
            {isSending ? "Sending" : "Send"}
          </button>
          <button
            type="button"
            className="lpt-save-button"
            disabled={isSaving}
            onClick={handleSave}
          >
            {isSaving ? "Saving" : "Save Request"}
          </button>
        </section>

        {saveStatus && (
          <section className="lpt-save-banner">
            Saved {saveStatus.requestName} to {saveStatus.relativePath}
          </section>
        )}

        {errorDetails && (
          <section className="lpt-error-banner" role="alert">
            <div className="lpt-error-banner-header">
              <strong>{errorDetails.title}</strong>
              <button
                type="button"
                className="lpt-error-clear"
                onClick={() => setErrorDetails(null)}
              >
                Clear
              </button>
            </div>
            <table className="lpt-error-table">
              <tbody>
                <tr>
                  <th>Message</th>
                  <td>{errorDetails.message}</td>
                </tr>
                {errorDetails.likelyCause && (
                  <tr>
                    <th>Likely cause</th>
                    <td>{errorDetails.likelyCause}</td>
                  </tr>
                )}
                <tr>
                  <th>Source</th>
                  <td>{errorDetails.source}</td>
                </tr>
                <tr>
                  <th>Event</th>
                  <td>
                    {errorDetails.eventType} / {errorDetails.eventTrigger}
                  </td>
                </tr>
                <tr>
                  <th>Request</th>
                  <td>
                    {errorDetails.method} {errorDetails.url}
                  </td>
                </tr>
                {errorDetails.status && (
                  <tr>
                    <th>Status</th>
                    <td>
                      {errorDetails.status} {errorDetails.statusText}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <details className="lpt-error-details">
              <summary>Stack trace and debug details</summary>
              <pre>{errorDetails.stack}</pre>
              <pre>{formatValue(errorDetails.debug)}</pre>
            </details>
          </section>
        )}

        <nav className="lpt-tabs" aria-label="Request sections">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              className={activeTab === tab ? "active" : ""}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>

        <section className="lpt-panel">
          {activeTab === "Request" && (
            <div className="lpt-grid-panel">
              <label>
                Method
                <select
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                >
                  {httpMethods.map((httpMethod) => (
                    <option key={httpMethod} value={httpMethod}>
                      {httpMethod}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Request URL
                <input
                  type="url"
                  value={requestUrl}
                  placeholder="https://api.example.com/resource"
                  onChange={(event) => setRequestUrl(event.target.value)}
                />
              </label>
              <label>
                Request name
                <input
                  type="text"
                  value={requestName}
                  placeholder="GET_healthcheck"
                  onChange={(event) => setRequestName(event.target.value)}
                />
              </label>
            </div>
          )}

          {activeTab === "Auth" && (
            <div className="lpt-auth-panel">
              <label>
                Authorization Bearer Token
                <textarea
                  value={authToken}
                  placeholder="eyJhbGciOi..."
                  onChange={(event) => setAuthToken(event.target.value)}
                />
              </label>
              <table className="lpt-preview-table">
                <tbody>
                  <tr>
                    <th>Header</th>
                    <td>Authorization</td>
                  </tr>
                  <tr>
                    <th>Value</th>
                    <td>{selectedHeaders.Authorization || ""}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "Headers" && (
            <>
              <table className="lpt-preview-table lpt-mandatory-table">
                <thead>
                  <tr>
                    <th>Mandatory Header</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {mandatoryHeaders.map((row) => (
                    <tr key={row.key}>
                      <td>{row.key}</td>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {renderRowsTable(
                headers,
                setHeaders,
                "Header name",
                "Header value"
              )}
              <button
                type="button"
                className="lpt-secondary-button"
                onClick={() => setHeaders([...headers, createRow()])}
              >
                Add Header
              </button>
            </>
          )}

          {activeTab === "Query Params" && (
            <>
              {renderRowsTable(
                queryParams,
                setQueryParams,
                "Parameter name",
                "Parameter value"
              )}
              <button
                type="button"
                className="lpt-secondary-button"
                onClick={() => setQueryParams([...queryParams, createRow()])}
              >
                Add Param
              </button>
            </>
          )}

          {activeTab === "Body" && (
            <label className="lpt-body-editor">
              Request Body
              <textarea
                value={body}
                disabled={method === "GET"}
                onChange={(event) => setBody(event.target.value)}
              />
            </label>
          )}

          {activeTab === "Response" && (
            <div className="lpt-response-panel">
              {isSending && (
                <div className="lpt-loading">
                  <ScaleLoader height="24px" width="8px" color="#0a60ff" />
                </div>
              )}
              {response ? (
                <>
                  <table className="lpt-preview-table">
                    <tbody>
                      <tr>
                        <th>Method</th>
                        <td>{response.method}</td>
                      </tr>
                      <tr>
                        <th>URL</th>
                        <td>{response.url}</td>
                      </tr>
                      <tr>
                        <th>Status</th>
                        <td>
                          {response.status} {response.statusText}
                        </td>
                      </tr>
                      {response.durationMs !== undefined && (
                        <tr>
                          <th>Time</th>
                          <td>{response.durationMs} ms</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className="lpt-response-grid">
                    <label>
                      Response Body
                      <pre>{formatValue(response.data)}</pre>
                    </label>
                    <label>
                      Response Headers
                      <pre>{formatValue(response.headers)}</pre>
                    </label>
                  </div>
                </>
              ) : (
                !isSending && <p className="lpt-empty-state">No response yet.</p>
              )}
            </div>
          )}

          {activeTab === "Debug Trace" && (
            <div className="lpt-debug-panel">
              {debugSteps.length ? (
                <table className="lpt-preview-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Status</th>
                      <th>Source</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debugSteps.map((step, index) => (
                      <tr key={`${step.time}-${index}`}>
                        <td>{step.time}</td>
                        <td>{step.status}</td>
                        <td>{step.source}</td>
                        <td>
                          <pre>{formatValue(step.details)}</pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="lpt-empty-state">
                  Send a request to see checkpoint logs.
                </p>
              )}
            </div>
          )}

          {activeTab === "Environment" && (
            <div className="lpt-environment-panel">
              <div className="lpt-environment-toolbar">
                <label>
                  Environment
                  <select
                    value={selectedEnvironment}
                    disabled={isLoadingEnvironment}
                    onChange={handleEnvironmentChange}
                  >
                    {environmentOptions.map((environmentName) => (
                      <option key={environmentName} value={environmentName}>
                        {environmentName}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="lpt-secondary-button"
                  disabled={isLoadingEnvironment}
                  onClick={() => loadEnvironment(selectedEnvironment)}
                >
                  {isLoadingEnvironment ? "Loading" : "Reload"}
                </button>
                <button
                  type="button"
                  className="lpt-save-button"
                  disabled={isSavingEnvironment}
                  onClick={handleSaveEnvironment}
                >
                  {isSavingEnvironment ? "Saving" : "Save Environment"}
                </button>
              </div>

              {environmentError && (
                <p className="lpt-save-error">{environmentError}</p>
              )}

              {environmentStatus && (
                <table className="lpt-preview-table lpt-environment-meta">
                  <tbody>
                    <tr>
                      <th>Environment</th>
                      <td>{environmentStatus.environmentName}</td>
                    </tr>
                    <tr>
                      <th>File</th>
                      <td>{environmentStatus.relativePath}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>{environmentStatus.action}</td>
                    </tr>
                    {environmentData?.date_modified && (
                      <tr>
                        <th>Modified</th>
                        <td>{environmentData.date_modified}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              <div className="lpt-table-scroll">{renderEnvironmentTable()}</div>
              <button
                type="button"
                className="lpt-secondary-button"
                onClick={() =>
                  setEnvironmentValues([
                    ...environmentValues,
                    createEnvironmentRow(),
                  ])
                }
              >
                Add Variable
              </button>
            </div>
          )}

          {activeTab === "Save" && (
            <div className="lpt-save-panel">
              <div className="lpt-grid-panel">
                <label>
                  Main folder
                  <input type="text" value="local_pulse_tool" readOnly />
                </label>
                <label>
                  API folder
                  <input
                    type="text"
                    value={collectionFolder}
                    onChange={(event) => setCollectionFolder(event.target.value)}
                  />
                </label>
                <label>
                  REST call name
                  <input
                    type="text"
                    value={requestName}
                    placeholder="GET_healthcheck"
                    onChange={(event) => setRequestName(event.target.value)}
                  />
                </label>
              </div>
              {saveError && <p className="lpt-save-error">{saveError}</p>}
              {saveStatus && (
                <table className="lpt-preview-table lpt-save-result-table">
                  <tbody>
                    <tr>
                      <th>Action</th>
                      <td>{saveStatus.action}</td>
                    </tr>
                    <tr>
                      <th>Request</th>
                      <td>{saveStatus.requestName}</td>
                    </tr>
                    <tr>
                      <th>File</th>
                      <td>{saveStatus.relativePath}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>
      </form>
      </div>
    </main>
  );
}

export default RestManStudioCollection;
